import { ArrowRight, ArrowLeft, HelpCircle } from "lucide-react";
import { MicButton } from "@/components/MicButton";
import { gentleHint, hasSorry, ASK_REPAIR } from "@/lib/sorry";

const ProblemInputs = ({ age, step, value, onChange }) => (
  <>
    <div className="pp-sorry-sentence" data-testid="sorry-problem-sentence">
      <span className="pp-sorry-starter">You probably feel</span>
      <input className="pp-input pp-blank" value={value.feel} placeholder="sad" data-testid="sorry-feel-input"
        onChange={(e) => onChange({ ...value, feel: e.target.value })} />
      <span className="pp-sorry-starter">, because I</span>
      <input className="pp-input pp-blank wide" value={value.did} placeholder="took your toy" data-testid="sorry-did-input"
        onChange={(e) => onChange({ ...value, did: e.target.value })} />
    </div>
    <div className="pp-presets">
      {step.examples[age].map(([feel, did], i) => (
        <button key={did} className="pp-pill" onClick={() => onChange({ feel, did })} data-testid={`sorry-example-0-${i}`}>
          You probably feel {feel}, because I {did}.
        </button>
      ))}
    </div>
  </>
);

const TextInput = ({ index, age, step, value, onChange }) => (
  <>
    <label className="pp-sorry-line">
      {step.starter && value !== ASK_REPAIR && <span className="pp-sorry-starter">{step.starter}</span>}
      <textarea rows={2} className="pp-textarea" value={value} data-testid={`sorry-input-${index}`}
        onChange={(e) => onChange(e.target.value)} placeholder={index === 1 ? "I'm sorry." : "…"} />
    </label>
    <div className="pp-presets">
      {step.examples[age].map((ex, i) => (
        <button key={ex} className="pp-pill" onClick={() => onChange(ex)} data-testid={`sorry-example-${index}-${i}`}>
          {step.starter ? `${step.starter} ${ex}` : ex}
        </button>
      ))}
      {step.key === "repair" && (
        <button className="pp-pill gold" onClick={() => onChange(ASK_REPAIR)} data-testid="sorry-ask-repair-button">
          <HelpCircle className="inline h-3.5 w-3.5 mr-1" /> Not sure? Ask: “{ASK_REPAIR}”
        </button>
      )}
    </div>
  </>
);

const valid = (step, value) => {
  if (step.kind === "problem") return value.feel.trim().length > 1 && value.did.trim().length > 1;
  if (step.key === "sorry") return hasSorry(value);
  return value.trim().length > 1;
};

export const SorryStepCard = ({ index, step, age, value, onChange, onNext, onBack }) => {
  const hint = gentleHint(step.kind === "problem" ? value.did : value);
  const onVoice = (t) => {
    if (step.kind !== "problem") return onChange(t.replace(/^to make it better,?\s*i will\s*/i, ""));
    const m = t.match(/feel\s+(.+?),?\s+because\s+i\s+(.+)/i);
    onChange(m ? { feel: m[1], did: m[2] } : { ...value, did: t });
  };
  return (
    <section className="pp-card" data-testid={`sorry-step-${index}`}>
      <p className="pp-label">Step {index + 1} of 3 · {step.name}</p>
      <h2 className="pp-h1 pp-sorry-title">{step.title}</h2>
      <p className="pp-sub">{step.help}</p>
      {step.kind === "problem"
        ? <ProblemInputs age={age} step={step} value={value} onChange={onChange} />
        : <TextInput index={index} age={age} step={step} value={value} onChange={onChange} />}
      {hint && <p className="pp-feedback small" data-testid="sorry-gentle-hint">{hint}</p>}
      {step.key === "sorry" && value && !hasSorry(value) && (
        <p className="pp-feedback small" data-testid="sorry-needs-sorry">This step needs the words “I'm sorry”.</p>
      )}
      <div className="pp-actions">
        {index > 0 && (
          <button className="pp-ghost" onClick={onBack} data-testid="sorry-back-button"><ArrowLeft className="h-4 w-4" /> Back</button>
        )}
        <MicButton testId={`sorry-mic-${index}`} onText={onVoice} label="Say it" />
        <button className="pp-primary" disabled={!valid(step, value)} onClick={onNext} data-testid="sorry-next-button">
          {index === 2 ? "Put it together" : "Next"} <ArrowRight className="h-5 w-5" />
        </button>
      </div>
    </section>
  );
};
